import { checkAmount, round2, type Check } from "../household/moneyInput";

export interface Category { id: number; name: string; perDay: boolean; perPerson: boolean; amount: string; /** What was really spent in total on this category (blank until known). */ actual: string }

// Lodging is per room, shared by the group; everything else is per person by default. Both switches are editable.
export const DEFAULT_CATEGORIES: Category[] = [
  { id: 1, name: "Lodging", perDay: true, perPerson: false, amount: "120", actual: "" },
  { id: 2, name: "Food", perDay: true, perPerson: true, amount: "50", actual: "" },
  { id: 3, name: "Local Transport", perDay: true, perPerson: true, amount: "20", actual: "" },
  { id: 4, name: "Activities", perDay: true, perPerson: true, amount: "40", actual: "" },
  { id: 5, name: "Flights", perDay: false, perPerson: true, amount: "450", actual: "" },
  { id: 6, name: "Travel Insurance", perDay: false, perPerson: true, amount: "60", actual: "" },
  { id: 7, name: "Shopping / Souvenirs", perDay: false, perPerson: true, amount: "100", actual: "" },
];

export const MAX_DAYS = 1000;
export const MAX_TRAVELERS = 1000;

export const checkDays = (raw: string): Check => checkAmount(raw, { label: "Trip length", integer: true, min: 1, max: MAX_DAYS, negative: true });
export const checkTravelers = (raw: string): Check => checkAmount(raw, { label: "Travelers", integer: true, min: 1, max: MAX_TRAVELERS, negative: true });

export interface RowResult { id: number; planned: number | null; actual: number | null; variance: number | null; amountError?: string; actualError?: string }

export interface TravelResult {
  rows: RowResult[];
  /** Null whenever days, travelers or any amount is invalid. */
  totals: null | { planned: number; perPerson: number; perDay: number; actual: number; actualCount: number; variance: number };
}

export function computeTravel(cats: Category[], daysRaw: string, travelersRaw: string): TravelResult {
  const d = checkDays(daysRaw), p = checkTravelers(travelersRaw);
  let bad = d.value === null || p.value === null;
  const rows: RowResult[] = cats.map((c) => {
    const a = checkAmount(c.amount, { label: c.name.trim() || "Amount" });
    const act = checkAmount(c.actual, { label: `${c.name.trim() || "Actual"} actual`, blank: NaN });
    if (a.value === null || act.value === null) bad = true;
    const planned = a.value === null || d.value === null || p.value === null ? null : round2(a.value * (c.perDay ? d.value : 1) * (c.perPerson ? p.value : 1));
    const actual = act.value === null || Number.isNaN(act.value) ? null : act.value;
    return { id: c.id, planned, actual, variance: planned !== null && actual !== null ? round2(planned - actual) : null, amountError: a.error, actualError: act.error };
  });
  if (bad || d.value === null || p.value === null) return { rows, totals: null };
  const planned = round2(rows.reduce((s, r) => s + (r.planned ?? 0), 0));
  const withActual = rows.filter((r) => r.actual !== null);
  const actual = round2(withActual.reduce((s, r) => s + (r.actual ?? 0), 0));
  const plannedOfThose = round2(withActual.reduce((s, r) => s + (r.planned ?? 0), 0));
  return { rows, totals: { planned, perPerson: round2(planned / p.value), perDay: round2(planned / d.value), actual, actualCount: withActual.length, variance: round2(plannedOfThose - actual) } };
}
